import { StaffScope } from '@constants/Settings/staffPermission.const'
import { parseUser } from '@services/api/auth.api'
import { getAccessToken } from '@services/auth/accessTokenStore'

/** Decode JWT payload (no signature verify) — same `scp` claim the API uses. */
function scopesFromAccessToken(accessToken: string | null): string[] {
  if (!accessToken) return []
  try {
    const segment = accessToken.split('.')[1]
    if (!segment) return []
    let base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const pad = base64.length % 4
    if (pad) base64 += '='.repeat(4 - pad)
    const json = JSON.parse(atob(base64)) as { scp?: string }
    if (typeof json.scp !== 'string' || !json.scp.trim()) return []
    return json.scp.split(/\s+/).filter(Boolean)
  } catch {
    return []
  }
}

function scopesFromStoredUser(): string[] {
  const user = parseUser()
  if (!user?.user_scope) return []

  const raw = user.user_scope
  if (Array.isArray(raw)) {
    return raw.filter((s): s is string => typeof s === 'string' && s.length > 0)
  }
  if (typeof raw === 'string') {
    return raw.split(/\s+/).filter(Boolean)
  }
  return []
}

export function getUserScopes(): string[] {
  const accessToken = getAccessToken()
  if (accessToken) {
    const fromJwt = scopesFromAccessToken(accessToken)
    if (fromJwt.length > 0) return fromJwt
    // Token present but no `scp` claim — use scopes from the login payload.
    return scopesFromStoredUser()
  }

  return scopesFromStoredUser()
}

/** Matches BE verifyScope: pass if token has any of the required scopes. */
export function hasStaffScope(...required: StaffScope[]): boolean {
  if (required.length === 0) return true
  const scopes = getUserScopes()
  return required.some((scope) => scopes.includes(scope))
}
